import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDate } from '../../util/datetime';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import * as tenantsApi from '../../api/tenants';
import { listLicenses, type License } from '../../api/licenses';
import { licensesByTenant, licenseStatus } from '../../components/license/LicenseSummary';
import type { Tenant } from '../../api/tenants';
import { RefreshButton } from '../../components/RefreshButton';
import { SortHeader } from '../../components/SortHeader';
import { useTableSort, sortRows } from '../../util/tableSort';
import { TenantFormDialog } from './TenantFormDialog';

type TenantSortKey =
  | 'name'
  | 'slug'
  | 'billing_mode'
  | 'status'
  | 'active'
  | 'created_at';

/** Order rank for the Status column: deleted < suspended < active. */
function statusRank(t: Tenant): number {
  if (t.deleted_at !== null) return 0;
  return t.is_active ? 2 : 1;
}

/**
 * Tenant list — the superadmin's main administration view.
 *
 * Each row carries three primary actions:
 *   - **Active** switch — toggles `is_active` (suspends/resumes the
 *     tenant without removing it). Visible state change is immediate;
 *     a failed PATCH reverts the local toggle.
 *   - **Edit** — opens the create/edit dialog with current values.
 *   - **Delete** — soft-deletes via DELETE /api/v1/tenants/{id}.
 *     Confirmation dialog explicit about reversibility: the slug + its
 *     Postgres schema remain locked, so the row can be restored by a
 *     manual SQL update but the tenant can't be re-created with the
 *     same slug. Hard-delete (drop schema + drop row) is a separate
 *     future operation gated behind a stronger confirm.
 */
export function TenantList() {
  // Sent here by RequireAuth when a platform admin opened a tenant-only page.
  const location = useLocation();
  const [needsSupportSession, setNeedsSupportSession] = useState(
    (location.state as { notice?: string } | null)?.notice === 'tenant-needs-support-session',
  );
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Tenant | null>(null);
  // Track which tenant rows are mid-toggle so we can dim the switch
  // and avoid double-firing on rapid clicks.
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [refreshing, setRefreshing] = useState(false);

  // Click-to-sort headers, matching the other admin list tables.
  const sort = useTableSort<TenantSortKey>('name');

  // Every tenant's license status (trials are licenses too). Best-effort:
  // a failure leaves the column blank rather than the list empty.
  const [licenses, setLicenses] = useState<Map<string, License> | null>(null);

  const reload = async () => {
    setError(null);
    setRefreshing(true);
    listLicenses().then(
      (ls) => setLicenses(licensesByTenant(ls)),
      () => setLicenses(null),
    );
    try {
      const rows = await tenantsApi.listTenants();
      setTenants(rows);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to load tenants',
      );
      setTenants([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const toggleActive = async (t: Tenant) => {
    setTogglingId(t.id);
    // Optimistic local update — the user sees the switch flip
    // immediately. If the server PATCH fails we revert below.
    setTenants((prev) =>
      prev
        ? prev.map((row) =>
            row.id === t.id ? { ...row, is_active: !row.is_active } : row,
          )
        : prev,
    );
    try {
      await tenantsApi.updateTenant(t.id, { is_active: !t.is_active });
    } catch (err: unknown) {
      // Revert and surface the error.
      setTenants((prev) =>
        prev
          ? prev.map((row) =>
              row.id === t.id ? { ...row, is_active: t.is_active } : row,
            )
          : prev,
      );
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to toggle tenant',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setError(null);
    try {
      await tenantsApi.deleteTenant(deleting.id);
      setDeleting(null);
      await reload();
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'failed to delete tenant',
      );
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">Tenants</Typography>
        <Stack direction="row" spacing={1}>
          <RefreshButton
            onClick={() => void reload()}
            refreshing={refreshing}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
          >
            New tenant
          </Button>
        </Stack>
      </Stack>

      {needsSupportSession && (
        <Alert severity="info" onClose={() => setNeedsSupportSession(false)} sx={{ mb: 2 }}>
          Tenant pages show a tenant's own data, which platform admins reach through a
          support session: open the tenant below and start one from its page.
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {tenants === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : tenants.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No tenants yet. Click <strong>New tenant</strong> to create the
            first organisation.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortHeader label="Name" col="name" sort={sort} />
                <SortHeader label="Slug" col="slug" sort={sort} />
                <SortHeader label="Billing" col="billing_mode" sort={sort} />
                <SortHeader label="Status" col="status" sort={sort} />
                <SortHeader label="Active" col="active" sort={sort} />
                <SortHeader label="Created" col="created_at" sort={sort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortRows(
                tenants,
                (t) => {
                  switch (sort.orderBy) {
                    case 'name':
                      return t.name;
                    case 'slug':
                      return t.slug;
                    case 'billing_mode':
                      return t.billing_mode;
                    case 'status':
                      return statusRank(t);
                    case 'active':
                      return t.is_active && t.deleted_at === null;
                    case 'created_at':
                      return t.created_at;
                  }
                },
                sort.order,
              ).map((t) => {
                const isDeleted = t.deleted_at !== null;
                return (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>
                      <code>{t.slug}</code>
                    </TableCell>
                    <TableCell>{t.billing_mode.replace('_', ' ')}</TableCell>
                    <TableCell>
                      {isDeleted ? (
                        <Chip label="Deleted" size="small" color="default" />
                      ) : t.is_active ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip label="Suspended" size="small" color="warning" />
                      )}
                      {licenses && !isDeleted && (() => {
                        const st = licenseStatus(licenses.get(t.id));
                        return (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={st.color}
                            label={st.label}
                            sx={{ ml: 0.5 }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          isDeleted
                            ? 'Tenant is soft-deleted'
                            : t.is_active
                            ? 'Click to suspend (blocks tenant access)'
                            : 'Click to re-enable'
                        }
                      >
                        <span>
                          <Switch
                            size="small"
                            checked={t.is_active && !isDeleted}
                            disabled={
                              isDeleted ||
                              togglingId === t.id
                            }
                            onChange={() => void toggleActive(t)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {formatDate(t.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <span>
                          <IconButton
                            onClick={() => setEditing(t)}
                            disabled={isDeleted}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete tenant">
                        <span>
                          <IconButton
                            onClick={() => setDeleting(t)}
                            disabled={isDeleted}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {creating && (
        <TenantFormDialog
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void reload();
          }}
        />
      )}
      {editing && (
        <TenantFormDialog
          mode="edit"
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete {deleting?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This soft-deletes the tenant. The row remains in the database
            so the slug <code>{deleting?.slug}</code> stays reserved and
            the per-tenant Postgres schema is preserved — but the tenant
            is removed from active listings and members can no longer
            access it.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }} color="text.secondary">
            If you need a true hard-delete (drop the schema + row), open
            an ops ticket; that's intentionally not exposed in the UI.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
